/* eslint-disable import/no-duplicates */
import {
  stack as d3Stack,
  stackOrderReverse,
  stackOffsetDiverging,
} from 'd3-shape';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import ClusteredColumnSeries from 'components/clustered-column-series';
import ColumnSeries from 'components/column-series';
import ColumnStepSeries from 'components/column-step-series';
import DifferenceColumnSeries from 'components/difference-column-series';
import DotSeries from 'components/dot-series';
import HorizontalGridLines from 'components/horizontal-grid-lines';
import LineSeries from 'components/line-series';
import LinearAxis from 'components/linear-axis';
import StackedColumnSeries from 'components/stacked-column-series';
import TimeAxis from 'components/time-axis';
import * as LayoutMode from 'constants/layout-mode';
import * as Orient from 'constants/orient';
import {BAR_SERIES} from 'constants/series-type';
import {BAR_STEP_SERIES} from 'constants/series-type';
import {CLUSTERED_COLUMN_SERIES} from 'constants/series-type';
import {COLUMN_SERIES} from 'constants/series-type';
import {COLUMN_STEP_SERIES} from 'constants/series-type';
import {DIFFERENCE_BAR_SERIES} from 'constants/series-type';
import {DIFFERENCE_COLUMN_SERIES} from 'constants/series-type';
import {DOT_SERIES} from 'constants/series-type';
import {LINE_SERIES} from 'constants/series-type';
import {STACKED_COLUMN_SERIES} from 'constants/series-type';
import linearAggregateDomain from 'domains/linear-aggregate-domain';
import linearDomain from 'domains/linear-domain';
import timeDomain from 'domains/time-domain';
import numericRange from 'ranges/numeric-range';
import linearScale from 'scales/linear-scale';
import timeScale from 'scales/time-scale';
import {getTranslate} from 'utils/attribute-util';
import Colors from 'common/constants/colors';
import styles from './index.css';

export default class TimeseriesGroup extends React.Component {
  static propTypes = {
    activeIndex: PropTypes.number,
    clampToFirstTimeDomain: PropTypes.bool,
    clampToZero: PropTypes.bool,
    contentPadding: PropTypes.objectOf(PropTypes.number),
    contentWidthMinusPadding: PropTypes.number,
    contentY: PropTypes.number,
    dividers: PropTypes.bool,
    domainY: PropTypes.array,
    end: PropTypes.string,
    gridlines: PropTypes.bool,
    gutters: PropTypes.objectOf(PropTypes.number),
    height: PropTypes.number,
    hideAxis: PropTypes.bool,
    index: PropTypes.number,
    label: PropTypes.string,
    layoutMode: PropTypes.oneOf(Object.values(LayoutMode)),
    numTicksX: PropTypes.number,
    numTicksY: PropTypes.number,
    series: PropTypes.array,
    start: PropTypes.string,
    tickWidth: PropTypes.number,
    type: PropTypes.oneOf([
      BAR_SERIES,
      BAR_STEP_SERIES,
      CLUSTERED_COLUMN_SERIES,
      COLUMN_SERIES,
      COLUMN_STEP_SERIES,
      DIFFERENCE_BAR_SERIES,
      DIFFERENCE_COLUMN_SERIES,
      LINE_SERIES,
      STACKED_COLUMN_SERIES,
    ]),
    width: PropTypes.number,
  };

  static defaultProps = {
    activeIndex: -1,
    clampToFirstTimeDomain: true,
    clampToZero: true,
    contentPadding: {},
    contentWidthMinusPadding: 0,
    contentY: 0,
    dividers: false,
    domainY: null,
    end: '',
    gridlines: false,
    gutters: {
      bottom: 0,
      left: 0,
      right: 0,
      top: 0,
    },
    height: 0,
    hideAxis: false,
    index: 0,
    label: '',
    layoutMode: LayoutMode.COLUMN_LAYOUT,
    numTicksX: 0,
    numTicksY: 5,
    series: [],
    start: '',
    tickWidth: 0,
    type: COLUMN_SERIES,
    width: 0,
  };

  getDomainX(series, clampToFirstTimeDomain, start, end) {
    const domainX = timeDomain(series, clampToFirstTimeDomain);
    domainX[0] = start ? moment(start) : domainX[0];
    domainX[1] = end ? moment(end) : domainX[1];
    return domainX;
  }

  getDomainY(series, clampToZero, type) {
    if (type === STACKED_COLUMN_SERIES) {
      return linearAggregateDomain(series, clampToZero);
    }
    return linearDomain(series, clampToZero);
  }

  getSeries(groupType, seriesType) {
    const type = seriesType ? seriesType : groupType;
    switch (type) {
      case CLUSTERED_COLUMN_SERIES:
        return ClusteredColumnSeries;
      case COLUMN_SERIES:
        return ColumnSeries;
      case COLUMN_STEP_SERIES:
        return ColumnStepSeries;
      case DIFFERENCE_COLUMN_SERIES:
        return DifferenceColumnSeries;
      case DOT_SERIES:
        return DotSeries;
      case LINE_SERIES:
        return LineSeries;
      case STACKED_COLUMN_SERIES:
        return StackedColumnSeries;
      default:
        return null;
    }
  }

  getSeriesWithLayout(series, type) {
    if (type === STACKED_COLUMN_SERIES) {
      // format series data to d3 stack input
      const stackInput = [];
      const keys = [];
      for (let sIdx = 0; sIdx < series.length; sIdx++) {
        keys.push(sIdx);
        for (let dIdx = 0; dIdx < series[sIdx].data.length; dIdx++) {
          const datum = series[sIdx].data[dIdx];
          if (!stackInput[dIdx]) {
            stackInput[dIdx] = {};
          }
          stackInput[dIdx][sIdx] = datum.value;
        }
      }

      const stack = d3Stack()
        .keys(keys)
        .offset(stackOffsetDiverging)
        .order(stackOrderReverse);
      const stackOutput = stack(stackInput);

      // write stack output to series
      for (let sIdx = 0; sIdx < series.length; sIdx++) {
        for (let dIdx = 0; dIdx < series[sIdx].data.length; dIdx++) {
          const seriesDatum = series[sIdx].data[dIdx];
          const stackDatum = stackOutput[sIdx][dIdx];
          seriesDatum.y = seriesDatum.value;
          seriesDatum.y0 = stackDatum[0] < 0 ? stackDatum[1] : stackDatum[0];
        }
      }
    }
    return series;
  }

  getTickPaddingX(bottom) {
    return Math.ceil(bottom / 5);
  }

  getTickPaddingY(index, gutters) {
    return index
      ? Math.ceil(gutters.right / 10)
      : Math.floor(gutters.left / 10);
  }

  getTimeAxisData(seriesList) {
    for (const series of seriesList) {
      if (series && series.hasOwnProperty('data') && series.data) {
        if (series.data.length && series.data[0].hasOwnProperty('tickFormat')) {
          return series.data;
        }
      }
    }
    return [];
  }

  shouldShowDividerAtZero(domainY) {
    if (domainY) {
      return domainY[0] < 0;
    }
    return false;
  }

  render() {
    const series = this.getSeriesWithLayout(this.props.series, this.props.type);
    const domainX = this.getDomainX(
      series,
      this.props.clampToFirstTimeDomain,
      this.props.start,
      this.props.end,
    );
    const domainY =
      this.props.domainY ||
      this.getDomainY(series, this.props.clampToZero, this.props.type);
    const rangeX = numericRange(this.props.contentWidthMinusPadding);
    const rangeY = numericRange(this.props.height, true);
    const scaleX = timeScale(domainX, rangeX);
    const scaleY = linearScale(domainY, rangeY);
    const zeroY = Math.round(scaleY(0));
    const tickPaddingX = this.getTickPaddingX(this.props.gutters.bottom);
    const tickPaddingY = this.getTickPaddingY(
      this.props.index,
      this.props.gutters,
    );
    const timeAxisData = this.getTimeAxisData(series);
    const showDividerAtZero = this.shouldShowDividerAtZero(domainY);
    return (
      <g className={styles.TimeseriesGroup}>
        {this.props.gridlines ? (
          <HorizontalGridLines
            numTicks={this.props.numTicksY}
            scale={scaleY}
            width={this.props.contentWidthMinusPadding}
          />
        ) : null}

        <g className={styles.TimeseriesContainer}>
          {series.map((datum, index) => {
            const TimeSeries = this.getSeries(this.props.type, datum.type);
            return (
              <TimeSeries
                activeIndex={this.props.activeIndex}
                animate={datum.animate}
                className={datum.className}
                columnSpacing={datum.columnSpacing}
                columnWidth={datum.columnWidth}
                comparisonData={datum.comparisonData}
                data={datum.data}
                height={this.props.height}
                id={datum.id}
                index={index}
                interactive={datum.interactive}
                key={index}
                layoutMode={this.props.layoutMode}
                marker={datum.marker}
                numSeries={this.props.series.length}
                scaleX={scaleX}
                scaleY={scaleY}
                style={datum.style}
                tickWidth={this.props.tickWidth}
                transform={getTranslate(this.props.contentPadding.left, 0)}
                type={datum.type || this.props.type}
                width={this.props.contentWidthMinusPadding}
                zeroY={zeroY}
              />
            );
          })}
        </g>

        {this.props.index === 0 && this.props.gutters.left ? (
          <LinearAxis
            divider={this.props.dividers}
            height={this.props.height}
            label={this.props.label}
            numTicks={this.props.numTicksY}
            orient={Orient.LEFT}
            scale={scaleY}
            showDividerAtZero={showDividerAtZero}
            style={{fill: Colors.gray400}}
            tickPadding={tickPaddingY}
            zeroY={zeroY}
          />
        ) : null}

        {this.props.index &&
        this.props.gutters.right &&
        !this.props.hideAxis ? (
          <LinearAxis
            divider={this.props.dividers}
            height={this.props.height}
            label={this.props.label}
            numTicks={this.props.numTicksY}
            orient={Orient.RIGHT}
            scale={scaleY}
            showDividerAtZero={false}
            style={{fill: Colors.gray400}}
            tickPadding={tickPaddingY}
            x={this.props.width}
            zeroY={zeroY}
          />
        ) : null}

        {this.props.index === 0 && this.props.gutters.bottom ? (
          <TimeAxis
            data={timeAxisData}
            divider={this.props.dividers}
            domain={domainX}
            layoutMode={this.props.layoutMode}
            numTicks={this.props.numTicksX}
            tickPadding={tickPaddingX}
            tickWidth={this.props.tickWidth}
            width={this.props.contentWidthMinusPadding}
            x={this.props.contentPadding.left}
            y={this.props.height}
          />
        ) : null}
      </g>
    );
  }
}
